import React, { useState } from "react"
import { Pressable, TouchableOpacity, View } from "react-native"
import {
  setCompUpdatingState,
  $selectedCompDetailId,
} from "@/stores/compsDataStore"
import CheckboxChecked from "@/assets/icons/checkbox-green-checked.svg"
import CheckboxUnchecked from "@/assets/icons/comp-cell-unselected.svg"
import { router, useLocalSearchParams } from "expo-router"
import {
  type AnalysisType,
  type AnalysisCompType,
  type ArvCompType,
} from "@/generated/graphql"
import { type CompSortCriterion } from "@/types"

import * as AnalyzerEvents from "@/analytics/events/analyzer"
import { H6, H7, Small } from "@/components/standard/BFText"
import { CompAvatarImage } from "@/components/analysis/CompAvatarImage"
import { compCompareText, type CompCompareTextOption } from "@/utils/CompUtils"
import { CopilotStep } from "react-native-copilot"
import ToolTips from "@/constants/ToolTips"
import { WalkthroughableView } from "@/components/copilot/WalkthroughableComponents"
import { useAnalyzeContext } from "@/components/analysis/AnalyzeContext"

interface CompCellProps {
  index: number
  analysis: AnalysisType
  comp: AnalysisCompType
  sortCriterion: CompSortCriterion
  onFirstSelection: (comp: AnalysisCompType) => void
  onToggleSelection: (comp: AnalysisCompType) => void
}

const CompsRow: React.FC<CompCellProps> = ({
  index,
  analysis,
  comp,
  onFirstSelection,
  onToggleSelection,
}) => {
  const [isSelected, setIsSelected] = useState(comp.isSelected)
  const { analysisId } = useLocalSearchParams()
  const isUserAdded: boolean = comp.compType === "manual"

  const arvcomp: ArvCompType | null = comp.arvcomp ?? null
  if (!arvcomp) return null

  if (!comp?.arvcomp) return null

  const analysisIdentifier: string =
    (Array.isArray(analysisId) ? analysisId[0] : analysisId) || "Error"

  const handleToggleSelection = () => {
    setCompUpdatingState(true)

    if (!showBackflipSuggestedComps) {
      setIsSelected(true)
      onFirstSelection(comp)
      return
    }

    setIsSelected((currentSelected) => !currentSelected)
    onToggleSelection(comp)
  }

  const handleCellPress = () => {
    AnalyzerEvents.compCardClicked({
      cardIndex: index,
      analysis,
      comp: {
        id: arvcomp.id,
        address: arvcomp?.fullAddress || "",
      },
    })

    $selectedCompDetailId.set(arvcomp.id)
    if (analysisIdentifier) {
      router.push(`/properties/${analysisIdentifier}/comp/${comp.arvcomp?.id}`)
    }
  }

  if (!arvcomp) {
    return null
  }

  const renderRow = (...input: string[]) => input.filter(Boolean).join(" • ")
  const compLabel = (label: CompCompareTextOption) =>
    compCompareText(label, comp, analysis) || ""

  const { isFirstManualAnalysisDisplay, showBackflipSuggestedComps } =
    useAnalyzeContext()

  return (
    <TouchableOpacity testID={`analysis-${index}`} onPress={handleCellPress}>
      <View
        className="border-light-grey border-t min-h-[110px] py-5"
        style={[
          {
            backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F4F4F4",
          },
        ]}
      >
        <View className="flex-1 flex-row items-center pl-4 pr-3">
          <CompAvatarImage comp={comp} analysis={analysis} className="mr-3" />

          <View className="flex-1">
            <H6>{renderRow(comp.arvcomp.street, compLabel("soldPrice"))}</H6>
            <Small numberOfLines={1} ellipsizeMode={"tail"}>
              {renderRow(
                compLabel("beds"),
                compLabel("baths"),
                compLabel("squareFeet"),
                compLabel("yearBuilt")
              )}
            </Small>
            <Small numberOfLines={1} ellipsizeMode={"tail"}>
              {renderRow(
                compLabel("status"),
                compLabel("soldDate"),
                compLabel("daysOnMarket"),
                compLabel("distance")
              )}
            </Small>
            <Small numberOfLines={1} ellipsizeMode={"tail"}>
              {renderRow(compLabel("pricePerSqft"))}
              {isUserAdded && (
                <>
                  <Small> • </Small>
                  <H7 className="text-calm-blue">User added comp</H7>
                </>
              )}
              {/* For testing... */}
              {/* <Tiny> {comp.arvcomp.updatedAt}</Tiny> */}
            </Small>
          </View>

          {index === 0 ? (
            <CopilotStep
              // @ts-expect-error on Copilot Step
              text={ToolTips.copilotStep3SelectComps}
              order={3}
              name="add comp button"
              active={isFirstManualAnalysisDisplay}
            >
              <WalkthroughableView>
                <ToggleSelectionButton
                  isSelected={showBackflipSuggestedComps ? isSelected : false}
                  onToggle={handleToggleSelection}
                />
              </WalkthroughableView>
            </CopilotStep>
          ) : (
            <ToggleSelectionButton
              isSelected={showBackflipSuggestedComps ? isSelected : false}
              onToggle={handleToggleSelection}
            />
          )}
        </View>
      </View>
    </TouchableOpacity>
  )
}

export default CompsRow

interface ToggleSelectionButtonProps {
  isSelected: boolean
  onToggle: () => void
}

const ToggleSelectionButton: React.FC<ToggleSelectionButtonProps> = ({
  isSelected,
  onToggle,
}) => (
  <Pressable
    testID={`${isSelected ? "uncheck" : "check"}-button`}
    onPress={onToggle}
    className="p-3"
  >
    {isSelected ? <CheckboxChecked /> : <CheckboxUnchecked />}
  </Pressable>
)
